import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { Icon } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { IMPORT_EXPORT_PROCESS_TYPE, LIST_TYPES } from 'Common/enums';
import { shouldShowListImportExport } from 'Selectors';
import { RoleService } from 'Services';
import EditListOverview from 'Routes/ListManagement/components/EditListOverview/EditListOverview';
import useGetSingleListAndSetBreadcrumb from 'Routes/ListManagement/routes/hooks/useGetSingleListAndSetBreadcrumb';
// eslint-disable-next-line max-len
import useGetUpdatedSingleListAndStartImportOrExport from 'Routes/ListManagement/routes/hooks/useGetUpdatedSingleListAndStartImportOrExport';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useExportListEntries from 'Hooks/query-hooks/listEntry/useExportListEntries';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';

import ListActionDropdown from 'Components/ListActionDropdown/ListActionDropdown';

type Props = {
    listId: number;
    isMissingConfigCustomList: boolean;
};

const ListActionButton = ({ listId, isMissingConfigCustomList }: Props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [showEditListOverviewModal, setShowEditListOverviewModal] = useState(false);
    const { users } = useGetReduxUsers();
    const { addAlert } = useAlertBanner();
    const isShowNewListActions = shouldShowListImportExport();
    const currentList = useGetSingleListAndSetBreadcrumb(users);
    const listData = currentList?.query?.data;
    const exportAllListEntriesMutation = useExportListEntries({});
    // prefer listId from hook since it will be the most updated/latest, else fall back to id from the prop
    const startExportEntries = useGetUpdatedSingleListAndStartImportOrExport({
        processType: IMPORT_EXPORT_PROCESS_TYPE.EXPORT,
        currentListId: listData?.id ?? listId,
        mutation: exportAllListEntriesMutation
    });

    const showModal = (listActionModal: boolean) => {
        setShowEditListOverviewModal(listActionModal);
    };
    const isListAdmin = RoleService.isListAdmin();

    const exportListAccess = () => {
        if (listData?.listType === LIST_TYPES.PAN) {
            // hide since PAN still doesn't support export feature.
            return false;
        }
        return isShowNewListActions && RoleService.hasListAccess;
    };

    const handleStartExport = async () => {
        if (listData) {
            // since we want to only export only if there are active entries
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Lists - Export Entire List',
                    text: 'Lists - Export Entire List'
                }
            });
            if (Number(listData.activeListEntriesCount) > 0) {
                await startExportEntries({
                    listId: listData.id,
                    listName: listData.name
                });
            } else {
                addAlert({
                    id: 'No entries to export error',
                    status: 'danger',
                    content: t('No entries to export')
                });
            }
        }
    };

    const handleImportEntries = (): void => {
        const currentPath = location.pathname;
        // since PAN doesn't support new import workflow yet.
        if (isShowNewListActions && listData?.listType !== LIST_TYPES.PAN) {
            history.push(`${currentPath}/import-entries`);
        } else {
            history.push(`${currentPath}/bulk-upload`);
        }
    };

    const options: MenuItem[] = [
        {
            visibility: exportListAccess(),
            dataTestId: 'export-all-entries-button',
            displayText: t('Export all entries to CSV'),
            iconElement: <Icon name='rectangle-stack-outline' size='md' aria-label='rectangle-stack-outline' />,
            disabled: exportAllListEntriesMutation.isLoading,
            handleNavigation: () => {
                handleStartExport();
            }
        },
        {
            visibility: RoleService.hasListAccess && !isMissingConfigCustomList,
            dataTestId: 'import-entries',
            displayText: t('Import entries from CSV'),
            iconElement: <Icon name='arrow-up-outline' size='md' aria-label='arrow-up-outline' />,
            handleNavigation: () => {
                handleImportEntries();
            }
        },
        {
            horizontalRule: isListAdmin,
            visibility: isListAdmin,
            displayText: t('Edit List Overview'),
            iconElement: <Icon name='pencil-1' size='md' aria-label='pencil-1' />,
            handleNavigation: () => {
                showModal(true);
            }
        }
    ];

    return (
        <>
            <ListActionDropdown
                iconElements
                id='rule-multi-option-button'
                variant='secondary'
                showArrows
                alignment='right'
                baseText={t('List Actions')}
                options={options}
                dataTestId='list-actions-button'
            />
            <EditListOverview
                data={listData as ListManagementApiList}
                modalStatus={showEditListOverviewModal}
                showModal={(listActionModal) => showModal(listActionModal)}
            />
        </>
    );
};

export default ListActionButton;
